import { useState, useCallback, useRef, useEffect } from 'react';
import { parseGS1Barcode, validateGTIN, GS1Data } from '../utils/gs1Parser';

/**
 * Parsed barcode result with strongly-typed fields
 * All fields are optional since not all barcodes contain all data
 */
export interface ParsedBarcodeData {
  // Raw input
  raw: string;

  // GS1 fields
  sscc?: string;
  gtin?: string;
  batchNo?: string;
  serialNo?: string;
  quantity?: number;
  expiryDate?: string; // ISO date string (YYYY-MM-DD)
  bestBeforeDate?: string;
  useByDate?: string;
  variantNumber?: string;
  netWeight?: number;
  grossWeight?: number;
  price?: number;
  pricePerUnit?: number;
  couponValue?: number;
  internalInfo?: string;

  // Metadata
  isGS1: boolean; // True if GS1 AIs were detected
  parsedAIs?: { [key: string]: string }; // Raw AI values

  // Validation
  hasValidGtin?: boolean; // True if GTIN checksum is valid
}

/**
 * Field names that can be required/optional
 */
export type BarcodeField = keyof Omit<ParsedBarcodeData, 'raw' | 'isGS1' | 'parsedAIs' | 'hasValidGtin'>;

/**
 * Validation result for a single field
 */
export interface FieldValidation {
  field: BarcodeField;
  required: boolean;
  present: boolean;
  valid: boolean;
  message?: string;
}

/**
 * Complete validation result
 */
export interface BarcodeValidationResult {
  isValid: boolean;
  fields: FieldValidation[];
  errors: string[];
  warnings: string[];
}

/**
 * Configuration options for barcode scanner
 */
export interface BarcodeScannerOptions {
  // Field requirements
  requiredFields?: BarcodeField[];
  optionalFields?: BarcodeField[];

  // Parsing options
  separators?: string; // GS1 separator characters (default: FNC1 + tilde + section sign)

  // Validation options
  validateGtin?: boolean; // Validate GTIN checksum (default: true)
  allowPlainBarcodes?: boolean; // Accept non-GS1 barcodes (default: true)

  // Behavior options
  clearOnScan?: boolean; // Clear input after successful scan (default: false)
  parseOnChange?: boolean; // Parse as user types (default: false, parse on Enter/blur)
  debounceMs?: number; // Debounce parsing when parseOnChange=true (default: 300)

  // Callbacks
  onScan?: (data: ParsedBarcodeData, validation: BarcodeValidationResult) => void;
  onError?: (error: Error) => void;
  onValidationFail?: (validation: BarcodeValidationResult) => void;
}

/**
 * Hook state
 */
export interface BarcodeScannerState {
  // Input state
  inputValue: string;

  // Parsed data
  data: ParsedBarcodeData | null;

  // Validation
  validation: BarcodeValidationResult | null;

  // UI state
  isValid: boolean;
  isParsing: boolean;
  hasScanned: boolean;

  // Error state
  error: Error | null;
}

/**
 * Hook return value
 */
export interface BarcodeScannerHook {
  // State
  state: BarcodeScannerState;

  // Input handlers
  inputValue: string;
  setInputValue: (value: string) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleBlur: () => void;

  // Manual control
  scan: (barcode: string) => void;
  clear: () => void;

  // Ref for input element
  inputRef: React.RefObject<HTMLInputElement>;

  // Field helpers
  getFieldValue: <T extends BarcodeField>(field: T) => ParsedBarcodeData[T] | undefined;
  hasField: (field: BarcodeField) => boolean;
  isFieldRequired: (field: BarcodeField) => boolean;
}

/**
 * Default options
 */
const DEFAULT_OPTIONS: Required<BarcodeScannerOptions> = {
  requiredFields: [],
  optionalFields: [],
  separators: String.fromCharCode(29) + '~§', // FNC1 + tilde + section sign
  validateGtin: true,
  allowPlainBarcodes: true,
  clearOnScan: false,
  parseOnChange: false,
  debounceMs: 300,
  onScan: () => {},
  onError: () => {},
  onValidationFail: () => {},
};

/**
 * Custom hook for barcode scanning with GS1-128 support
 *
 * @example
 * // Simple usage - just get the data
 * const { inputValue, handleChange, handleKeyDown, state } = useBarcodeScanner({
 *   onScan: (data) => console.log(data.gtin)
 * });
 *
 * @example
 * // With field requirements
 * const scanner = useBarcodeScanner({
 *   requiredFields: ['gtin'],
 *   optionalFields: ['batchNo', 'serialNo'],
 *   onScan: (data, validation) => {
 *     if (validation.isValid) {
 *       processItem(data);
 *     }
 *   }
 * });
 */
export function useBarcodeScanner(options: BarcodeScannerOptions = {}): BarcodeScannerHook {
  // Merge options with defaults
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // State
  const [inputValue, setInputValue] = useState('');
  const [data, setData] = useState<ParsedBarcodeData | null>(null);
  const [validation, setValidation] = useState<BarcodeValidationResult | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [hasScanned, setHasScanned] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Convert GS1Data to ParsedBarcodeData with validation
   */
  const convertToParsedData = useCallback((raw: string, gs1Data: GS1Data): ParsedBarcodeData => {
    const hasValidGtin = gs1Data.gtin && opts.validateGtin
      ? validateGTIN(gs1Data.gtin)
      : undefined;

    return {
      raw,
      sscc: gs1Data.sscc,
      gtin: gs1Data.gtin,
      batchNo: gs1Data.batchNo,
      serialNo: gs1Data.serialNo,
      quantity: gs1Data.quantity,
      expiryDate: gs1Data.expiryDate,
      bestBeforeDate: gs1Data.bestBeforeDate,
      useByDate: gs1Data.useByDate,
      variantNumber: gs1Data.variantNumber,
      netWeight: gs1Data.netWeight,
      grossWeight: gs1Data.grossWeight,
      price: gs1Data.price,
      pricePerUnit: gs1Data.pricePerUnit,
      couponValue: gs1Data.couponValue,
      internalInfo: gs1Data.internalInfo,
      isGS1: Object.keys(gs1Data.parsedAIs || {}).length > 0,
      parsedAIs: gs1Data.parsedAIs,
      hasValidGtin,
    };
  }, [opts.validateGtin]);

  /**
   * Validate parsed data against field requirements
   */
  const validateData = useCallback((parsedData: ParsedBarcodeData): BarcodeValidationResult => {
    const fields: FieldValidation[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check required fields
    for (const field of opts.requiredFields) {
      const value = parsedData[field];
      const present = value !== undefined && value !== null;
      const valid = present;

      fields.push({
        field,
        required: true,
        present,
        valid,
        message: valid ? undefined : `Required field '${field}' is missing`,
      });

      if (!valid) {
        errors.push(`Missing required field: ${field}`);
      }
    }

    // Check optional fields (just track presence)
    for (const field of opts.optionalFields) {
      const value = parsedData[field];
      const present = value !== undefined && value !== null;

      fields.push({
        field,
        required: false,
        present,
        valid: true, // Optional fields are always "valid"
      });
    }

    // GTIN validation
    if (parsedData.gtin && opts.validateGtin && parsedData.hasValidGtin === false) {
      errors.push('Invalid GTIN checksum');
    }

    // Plain barcode warning
    if (!parsedData.isGS1 && !opts.allowPlainBarcodes) {
      errors.push('Non-GS1 barcodes are not allowed');
    } else if (!parsedData.isGS1 && opts.allowPlainBarcodes) {
      warnings.push('Plain barcode detected (no GS1 structure)');
    }

    const isValid = errors.length === 0;

    return {
      isValid,
      fields,
      errors,
      warnings,
    };
  }, [opts.requiredFields, opts.optionalFields, opts.validateGtin, opts.allowPlainBarcodes]);

  /**
   * Parse barcode and update state
   */
  const parseBarcodeInternal = useCallback((barcode: string) => {
    if (!barcode.trim()) {
      setData(null);
      setValidation(null);
      setError(null);
      return;
    }

    setIsParsing(true);
    setError(null);

    try {
      // Parse GS1
      const gs1Data = parseGS1Barcode(barcode, opts.separators);

      // Convert to ParsedBarcodeData
      const parsedData = convertToParsedData(barcode, gs1Data);

      // Validate
      const validationResult = validateData(parsedData);

      // Update state
      setData(parsedData);
      setValidation(validationResult);
      setHasScanned(true);

      // Callbacks
      if (validationResult.isValid) {
        opts.onScan(parsedData, validationResult);

        // Clear input if configured
        if (opts.clearOnScan) {
          setInputValue('');
        }
      } else {
        opts.onValidationFail(validationResult);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      opts.onError(error);
    } finally {
      setIsParsing(false);
    }
  }, [opts, convertToParsedData, validateData]);

  /**
   * Public scan method
   */
  const scan = useCallback((barcode: string) => {
    parseBarcodeInternal(barcode);
  }, [parseBarcodeInternal]);

  /**
   * Clear all state
   */
  const clear = useCallback(() => {
    setInputValue('');
    setData(null);
    setValidation(null);
    setError(null);
    setHasScanned(false);
    inputRef.current?.focus();
  }, []);

  /**
   * Handle input change
   */
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Parse on change if enabled
    if (opts.parseOnChange) {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        parseBarcodeInternal(value);
      }, opts.debounceMs);
    }
  }, [opts.parseOnChange, opts.debounceMs, parseBarcodeInternal]);

  /**
   * Handle key down (Enter to scan)
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !opts.parseOnChange) {
      e.preventDefault();
      parseBarcodeInternal(inputValue);
    }
  }, [inputValue, opts.parseOnChange, parseBarcodeInternal]);

  /**
   * Handle blur (scan if not parseOnChange)
   */
  const handleBlur = useCallback(() => {
    if (!opts.parseOnChange && inputValue.trim()) {
      parseBarcodeInternal(inputValue);
    }
  }, [inputValue, opts.parseOnChange, parseBarcodeInternal]);

  /**
   * Get field value helper
   */
  const getFieldValue = useCallback(<T extends BarcodeField>(field: T): ParsedBarcodeData[T] | undefined => {
    return data?.[field];
  }, [data]);

  /**
   * Check if field is present
   */
  const hasField = useCallback((field: BarcodeField): boolean => {
    const value = data?.[field];
    return value !== undefined && value !== null;
  }, [data]);

  /**
   * Check if field is required
   */
  const isFieldRequired = useCallback((field: BarcodeField): boolean => {
    return opts.requiredFields.includes(field);
  }, [opts.requiredFields]);

  /**
   * Cleanup debounce timer
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Compute derived state
  const isValid = validation?.isValid ?? false;

  return {
    state: {
      inputValue,
      data,
      validation,
      isValid,
      isParsing,
      hasScanned,
      error,
    },
    inputValue,
    setInputValue,
    handleChange,
    handleKeyDown,
    handleBlur,
    scan,
    clear,
    inputRef,
    getFieldValue,
    hasField,
    isFieldRequired,
  };
}
